import React, { useState, useEffect } from 'react';
import { TestConfig, TestVariant } from '../types';
import FigmaEmbed from './FigmaEmbed';
import SurveyForm from './SurveyForm';
import ThankYou from './ThankYou';
import { clearAllData } from '../utils/storage';

interface PrototypeTestProps {
  config: TestConfig;
  onReset?: () => void;
  onProgressUpdate?: (progress: number, stageName: string) => void;
}

export default function PrototypeTest({ config, onReset, onProgressUpdate }: PrototypeTestProps) {
  enum TestStage {
    LOADING,
    INSTRUCTIONS,
    PROTOTYPE_A,
    SURVEY_A,
    PROTOTYPE_B,
    SURVEY_B,
    COMPLETE
  }

  const [stage, setStage] = useState<TestStage>(TestStage.INSTRUCTIONS);
  const [currentVariantIndex, setCurrentVariantIndex] = useState(0);

  useEffect(() => {
    // Calculate progress based on current stage
    const totalStages = 6; // Instructions, Proto A, Survey A, Proto B, Survey B, Complete
    let progress = 0;
    let stageName = '';

    switch (stage) {
      case TestStage.INSTRUCTIONS:
        progress = 0;
        stageName = 'Getting Started';
        break;
      case TestStage.PROTOTYPE_A:
        progress = 20;
        stageName = 'Explore Design';
        break;
      case TestStage.SURVEY_A:
        progress = 40;
        stageName = 'Feedback';
        break;
      case TestStage.PROTOTYPE_B:
        progress = 60;
        stageName = 'Explore Design';
        break;
      case TestStage.SURVEY_B:
        progress = 80;
        stageName = 'Feedback';
        break;
      case TestStage.COMPLETE:
        progress = 100;
        stageName = 'Complete';
        break;
    }

    if (onProgressUpdate) {
      onProgressUpdate(progress, stageName);
    }
  }, [stage, onProgressUpdate]);

  const handleComplete = (currentStage: TestStage) => {
    switch (currentStage) {
      case TestStage.INSTRUCTIONS:
        setStage(TestStage.PROTOTYPE_A);
        break;
      case TestStage.PROTOTYPE_A:
        setStage(TestStage.SURVEY_A);
        break;
      case TestStage.SURVEY_A:
        setStage(TestStage.PROTOTYPE_B);
        setCurrentVariantIndex(1);
        break;
      case TestStage.PROTOTYPE_B:
        setStage(TestStage.SURVEY_B);
        break;
      case TestStage.SURVEY_B:
        setStage(TestStage.COMPLETE);
        break;
      default:
        break;
    }
  };

  const handleReset = () => {
    if (onReset) {
      onReset();
    } else {
      setStage(TestStage.INSTRUCTIONS);
      setCurrentVariantIndex(0);
    }
  };

  const handleClearData = () => {
    clearAllData();
    window.location.reload();
  };

  const currentVariant = config.variants[currentVariantIndex];

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-8 animate-fadeIn">
      {stage === TestStage.INSTRUCTIONS && (
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8 mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {config.title}
          </h1>
          <div className="prose text-gray-600 mb-6">
            <p>{config.description}</p>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  This is a prototype, so some interactions are simulated. Focus on the overall user experience and flow rather than technical functionality.
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => handleComplete(TestStage.INSTRUCTIONS)}
            className="w-full sm:w-auto px-6 py-3 bg-blue-500 text-white font-medium rounded-lg shadow-md hover:bg-blue-600 transition-colors duration-300"
          >
            Get Started
          </button>
        </div>
      )}

      {(stage === TestStage.PROTOTYPE_A || stage === TestStage.PROTOTYPE_B) && currentVariant && (
        <FigmaEmbed
          variant={currentVariant}
          onComplete={() => handleComplete(stage === TestStage.PROTOTYPE_A ? TestStage.PROTOTYPE_A : TestStage.PROTOTYPE_B)}
        />
      )}

      {(stage === TestStage.SURVEY_A || stage === TestStage.SURVEY_B) && currentVariant && (
        <SurveyForm
          questions={config.questions}
          variant={currentVariant}
          onComplete={() => handleComplete(stage === TestStage.SURVEY_A ? TestStage.SURVEY_A : TestStage.SURVEY_B)}
        />
      )}

      {stage === TestStage.COMPLETE && (
        <ThankYou onReset={handleReset} />
      )}
    </div>
  );
}